import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { previewTakeDownSet, takeDownSet } from '@/lib/api/generated/api';
import type {
  LibraryItemKind,
  LibraryPieceRef,
  TakeDownPreview,
  TakeDownResult,
} from '@/lib/api/generated/model';
import { libraryKeys } from '../queryKeys';

class TakeDownError extends Error {
  readonly code: string | null;

  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'TakeDownError';
    this.code = code;
  }
}

/**
 * Say that a take-down answer came back unlike the contract, or that the service
 * failed. The person still sees a refusal; this is for whoever has to find out
 * why.
 */
function reportOdd(problem: string, body: unknown): void {
  console.warn(`take down: ${problem}`, body);
}

function refusal(
  status: number,
  data: unknown,
  fallback: string,
): TakeDownError {
  const body = typeof data === 'object' && data !== null ? data : {};
  const message =
    'message' in body && typeof body.message === 'string' && body.message
      ? body.message
      : null;
  const code =
    'error' in body && typeof body.error === 'string' ? body.error : null;
  if (status >= 500 || message === null || code === null) {
    reportOdd(`${String(status)} ${code ?? 'without a code'}`, data);
  }
  return new TakeDownError(message ?? fallback, code);
}

/**
 * Taking a shared item out of the Library, with the linked pieces only it uses
 * (spec 213; service spec 167).
 *
 * Only the person who shared it, or an org admin, is offered this — the catalog
 * says so per item — and the service checks again. While the confirmation is
 * open, the service is asked which linked pieces the same person shared would
 * go with it. Take down sends the ones still ticked; the service re-checks, and
 * if the answer changed in between it refuses, and the question is asked again.
 * Workspaces that installed any of them keep their copies.
 */
export function useTakeDownSet(
  workspaceId: string,
  item: { kind: LibraryItemKind; key: string },
  open: boolean,
) {
  const queryClient = useQueryClient();
  const preview = useQuery({
    queryKey: libraryKeys.takeDownPreview(workspaceId, item.kind, item.key),
    queryFn: async (): Promise<TakeDownPreview> => {
      const response = await previewTakeDownSet(
        workspaceId,
        item.kind,
        item.key,
      );
      if (response.status === 200) return response.data;
      throw refusal(
        response.status,
        response.data,
        'What goes with it could not be checked.',
      );
    },
    enabled: open,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
  const mutation = useMutation({
    mutationFn: async (also: LibraryPieceRef[]): Promise<TakeDownResult> => {
      const response = await takeDownSet(workspaceId, item.kind, item.key, {
        also,
      });
      if (response.status === 200) return response.data;
      throw refusal(
        response.status,
        response.data,
        'It could not be taken down.',
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: libraryKeys.items(workspaceId),
      }),
    onError: (error) => {
      // The answer changed since it was asked: ask again.
      if (
        error instanceof TakeDownError &&
        error.code === 'TAKE_DOWN_SET_CHOICE_INVALID'
      ) {
        void preview.refetch();
      }
    },
  });
  return {
    // A failed check offers nothing, not the answer from before it: the
    // service may have refused the old one, so it is no longer the set.
    offer: preview.isError ? undefined : preview.data,
    checking: preview.isFetching,
    checkFailed: preview.isError,
    /** ``onDone`` runs once they are gone; a refusal stays on screen instead. */
    takeDown: (
      also: LibraryPieceRef[],
      onDone: (result: TakeDownResult) => void,
    ) => {
      mutation.mutate(also, { onSuccess: onDone });
    },
    pending: mutation.isPending,
    error: mutation.error?.message,
  };
}
